import type { Announcements, DndContextProps, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    rectIntersection,
    TouchSensor,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ScrollArea, Scrollbar } from '@radix-ui/react-scroll-area';
import { createContext, type HTMLAttributes, type ReactNode, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import tunnel from 'tunnel-rat';

import { cn } from '@/utils/cn';
import { Card } from 'widgets';

export type { DragEndEvent } from '@dnd-kit/core';

const t = tunnel();
type KanbanItemProps = {
    id: string;
    name: string;
    column: string;
} & Record<string, unknown>;
type KanbanColumnProps = {
    id: string;
    name: string;
} & Record<string, unknown>;
type KanbanContextProps<
    T extends KanbanItemProps = KanbanItemProps,
    C extends KanbanColumnProps = KanbanColumnProps,
> = {
    columns: C[];
    data: T[];
    activeCardId: string | null;
};
const KanbanContext = createContext<KanbanContextProps>({
    columns: [],
    data: [],
    activeCardId: null,
});
export type KanbanBoardProps = {
    id: string;
    children: ReactNode;
    className?: string;
};
export const KanbanBoard = ({ id, children, className }: KanbanBoardProps) => {
    const { isOver, setNodeRef } = useDroppable({
        id,
        data: {
            type: 'column',
            columnId: id,
        },
    });
    return (
        <div
            className={cn(
                'bg-secondary flex h-full min-h-40 min-w-0 flex-col divide-y overflow-hidden rounded-md border text-xs shadow-sm ring-2 transition-all',
                isOver ? 'ring-primary' : 'ring-transparent',
                className,
            )}
            ref={setNodeRef}
        >
            {children}
        </div>
    );
};
export type KanbanCardProps<T extends KanbanItemProps = KanbanItemProps> = T & {
    children?: ReactNode;
    className?: string;
};
export const KanbanCard = <T extends KanbanItemProps = KanbanItemProps>({
    id,
    name,
    children,
    className,
}: KanbanCardProps<T>) => {
    const { attributes, listeners, setNodeRef, transition, transform, isDragging } = useSortable({
        id,
    });
    const { activeCardId } = useContext(KanbanContext);
    const style = {
        transition,
        transform: CSS.Transform.toString(transform),
    };
    return (
        <>
            <div style={style} {...listeners} {...attributes} ref={setNodeRef}>
                <Card
                    className={cn(
                        'cursor-grab gap-4 rounded-md p-3 shadow-sm',
                        isDragging && 'pointer-events-none cursor-grabbing opacity-30',
                        className,
                    )}
                >
                    {children ?? <p className="m-0 text-sm font-medium">{name}</p>}
                </Card>
            </div>
            {activeCardId === id && (
                <t.In>
                    <Card
                        className={cn(
                            'ring-primary cursor-grab gap-4 rounded-md p-3 shadow-sm ring-2',
                            isDragging && 'cursor-grabbing',
                            className,
                        )}
                    >
                        {children ?? <p className="m-0 text-sm font-medium">{name}</p>}
                    </Card>
                </t.In>
            )}
        </>
    );
};
export type KanbanCardsProps<T extends KanbanItemProps = KanbanItemProps> = Omit<
    HTMLAttributes<HTMLDivElement>,
    'children' | 'id'
> & {
    children: (item: T) => ReactNode;
    id: string;
};
export const KanbanCards = <T extends KanbanItemProps = KanbanItemProps>({
    children,
    className,
    ...props
}: KanbanCardsProps<T>) => {
    const { data } = useContext(KanbanContext) as KanbanContextProps<T>;
    const filteredData = data.filter((item) => item.column === props.id);
    const items = filteredData.map((item) => item.id);
    return (
        <ScrollArea className="h-full overflow-auto">
            <SortableContext items={items}>
                <div className={cn('flex flex-col gap-2 p-2 pb-4', className)} {...props}>
                    {filteredData.map(children)}
                </div>
            </SortableContext>
            <Scrollbar orientation="vertical" />
        </ScrollArea>
    );
};
export type KanbanHeaderProps = HTMLAttributes<HTMLDivElement>;
export const KanbanHeader = ({ className, ...props }: KanbanHeaderProps) => (
    <div className={cn('m-0 p-2 text-sm font-semibold', className)} {...props} />
);
export type KanbanProviderProps<
    T extends KanbanItemProps = KanbanItemProps,
    C extends KanbanColumnProps = KanbanColumnProps,
> = Omit<DndContextProps, 'children'> & {
    children: (column: C) => ReactNode;
    className?: string;
    columns: C[];
    data: T[];
    onDataChange?: (data: T[]) => void;
    onDragStart?: (event: DragStartEvent) => void;
    onDragEnd?: (event: DragEndEvent) => void;
    onDragOver?: (event: DragOverEvent) => void;
};
export const KanbanProvider = <
    T extends KanbanItemProps = KanbanItemProps,
    C extends KanbanColumnProps = KanbanColumnProps,
>({
    children,
    onDragStart,
    onDragEnd,
    onDragOver,
    className,
    columns,
    data,
    onDataChange,
    ...props
}: KanbanProviderProps<T, C>) => {
    const [activeCardId, setActiveCardId] = useState<string | null>(null);
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor), useSensor(KeyboardSensor));
    const handleDragStart = (event: DragStartEvent) => {
        const card = data.find((item) => item.id === event.active.id);
        if (card) {
            setActiveCardId(event.active.id as string);
        }
        onDragStart?.(event);
    };
    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        if (!over) {
            return;
        }
        const activeItem = data.find((item) => item.id === active.id);
        const overItem = data.find((item) => item.id === over.id);
        if (!(activeItem && overItem)) {
            return;
        }
        const activeColumn = activeItem.column;
        const overColumn = overItem.column;
        if (activeColumn !== overColumn) {
            let newData = [...data];
            const activeIndex = newData.findIndex((item) => item.id === active.id);
            const overIndex = newData.findIndex((item) => item.id === over.id);
            newData[activeIndex].column = overColumn;
            newData = arrayMove(newData, activeIndex, overIndex);
            onDataChange?.(newData);
        }
        onDragOver?.(event);
    };
    const handleDragEnd = (event: DragEndEvent) => {
        setActiveCardId(null);
        onDragEnd?.(event);
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        let newData = [...data];
        const oldIndex = newData.findIndex((item) => item.id === active.id);
        const newIndex = newData.findIndex((item) => item.id === over.id);
        newData = arrayMove(newData, oldIndex, newIndex);
        onDataChange?.(newData);
    };
    const announcements: Announcements = {
        onDragStart({ active }) {
            const { name, column } = data.find((item) => item.id === active.id) ?? {};
            return `Picked up the card "${name}" from the "${column}" column`;
        },
        onDragOver({ active, over }) {
            const { name } = data.find((item) => item.id === active.id) ?? {};
            const newColumn = columns.find((column) => column.id === over?.id)?.name;
            return `Dragged the card "${name}" over the "${newColumn}" column`;
        },
        onDragEnd({ active, over }) {
            const { name } = data.find((item) => item.id === active.id) ?? {};
            const newColumn = columns.find((column) => column.id === over?.id)?.name;
            return `Dropped the card "${name}" into the "${newColumn}" column`;
        },
        onDragCancel({ active }) {
            const { name } = data.find((item) => item.id === active.id) ?? {};
            return `Cancelled dragging the card "${name}"`;
        },
    };
    return (
        <KanbanContext.Provider value={{ columns, data, activeCardId }}>
            <DndContext
                accessibility={{ announcements }}
                collisionDetection={rectIntersection}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragStart={handleDragStart}
                sensors={sensors}
                {...props}
            >
                <div className={cn('grid h-full w-max min-w-full auto-cols-[14rem] grid-flow-col gap-4', className)}>
                    {columns.map((column) => children(column))}
                </div>
                {typeof window !== 'undefined' &&
                    (createPortal(
                        <DragOverlay>
                            <t.Out />
                        </DragOverlay>,
                        document.body,
                    ) as React.ReactNode)}
            </DndContext>
        </KanbanContext.Provider>
    );
};
